import { TableAddRowOptions, TableAddRowResult, TableRow, TableRowMode, TableRowProps, TableRowDisplayEvent, Textbox, DropdownSelectionEvent, ClickEvent, DropdownItem, BlurEvent, TableSelectionEvent } from "@mcleod/components";
import { ModelRow, Api, StringUtil } from "@mcleod/core";
import { AutogenLayoutEdiCustomFilename } from "./autogen/AutogenLayoutEdiCustomFilename";
import { RowCustomFilename } from "./models/ModelCustomFilename";
import { CommonDialogs } from "@mcleod/common";

export class EdiCustomFilename extends AutogenLayoutEdiCustomFilename {
    private _isImageType = false;
    private _fileMask: string = "";
    private _batchno: number = 0;
    private _forbidden = new Set(["*", "(", ")", "%", "!", "@", "^", "$", "#", "&", "\"", "'", "/", "\\"]);

    // Button member variable
    public buttonClear: any;

    // Node type constants
    private static readonly DATE_NODE = "D";
    private static readonly TIME_NODE = "T";
    private static readonly OPTIONAL_NODE = "O";
    private static readonly BATCH_NODE = "B";
    private static readonly SHIPMENT_NODE = "S";
    private static readonly BOL_NODE = "L";
    private static readonly ORDER_NODE = "R";
    private static readonly INVOICE_NODE = "I";
    private static readonly IMAGE_DOC_NODE = "M";


    public get fileMask(): string {
        return this._fileMask;
    }
    public set fileMask(value: string) {
        this._fileMask = value;
        this.parseFileMask(value);
    }

    public get isImageType(): boolean {
        return this._isImageType;
    }

    public set isImageType(value: boolean) {
        this._isImageType = value;
    }

    public get batchno(): number {
        return this._batchno;
    }

    public set batchno(value: number) {
        this._batchno = value;
    }

    /**
     * Parses a file mask string and populates the data source rows and screen fields
     * Example: <PRE>reply:<BATCHNO>:<DATE>yyyyMMdd:<OPT>blarblar:<OPT>diddyblarblar:<TIME>HHMMSSmmm:<SUF>txt:<DEL>.:<EXCLDATEDEL>
     */
    public parseFileMask(fileMask: string): void {
        if (!fileMask || fileMask.trim() === "") {
            return;
        }

        // Clear existing data
        this.tableFilename.clearRows();

        // Split the mask by colons to get individual components
        const components = fileMask.split(':');
        let sequence = 1;
        let hasExcludeDateDelimiter = false;


        hasExcludeDateDelimiter = fileMask.includes('<EXCLDATEDEL>');


        for (const component of components) {
            const trimmedComponent = component.trim();
            if (!trimmedComponent) continue;


            // Parse PRE (Prefix)
            if (trimmedComponent.startsWith('<PRE>')) {
                const prefixValue = trimmedComponent.substring(5); // Remove '<PRE>'
                this.textboxPrefix.text = prefixValue;
                continue;
            }

            // Parse SUF (Suffix/Extension)
            if (trimmedComponent.startsWith('<SUF>')) {
                const suffixValue = trimmedComponent.substring(5); // Remove '<SUF>'
                this.textboxExtension.text = suffixValue;
                continue;
            }

            // Parse DEL (Delimiter)
            if (trimmedComponent.startsWith('<DEL>')) {
                const delimiterValue = trimmedComponent.substring(5); // Remove '<DEL>'
                this.textboxNodeDelimiter.text = delimiterValue;
                // Set the selected item if the delimiter value matches one of the dropdown options
                if (delimiterValue) {
                    this.textboxNodeDelimiter.setSelectedItemFromValue(delimiterValue);
                }
                continue;
            }

            // Parse BATCHNO
            if (trimmedComponent === '<BATCHNO>') {
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.BATCH_NODE,
                    node_config: "",
                    filemask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

            // Parse DATE
            if (trimmedComponent.startsWith('<DATE>')) {
                const dateConfig = trimmedComponent.substring(6); // Remove '<DATE>'
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.DATE_NODE,
                    node_config: dateConfig || "yyyyMMdd",
                    filemask: ""
                });

                // Set exclude delimiter if EXCLDATEDEL tag was present
                if (hasExcludeDateDelimiter) {
                    rowData.data["exclude_delimiter"] = "Y";
                }

                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

            // Parse TIME
            if (trimmedComponent.startsWith('<TIME>')) {
                const timeConfig = trimmedComponent.substring(6); // Remove '<TIME>'
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.TIME_NODE,
                    node_config: timeConfig || "HHMMSS",
                    filemask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

            // Parse OPT (Optional)
            if (trimmedComponent.startsWith('<OPT>')) {
                const optionalValue = trimmedComponent.substring(5); // Remove '<OPT>'
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.OPTIONAL_NODE,
                    node_config: optionalValue,
                    filemask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

            if (trimmedComponent.startsWith('<BOL>')) {
                const optionalValue = trimmedComponent.substring(5); // Remove '<BOL>'
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.BOL_NODE,
                    node_config: optionalValue,
                    filemask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

            if (trimmedComponent.startsWith('<ORDERID>')) {
                const optionalValue = trimmedComponent.substring(9); // Remove '<ORDERID>'
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.ORDER_NODE,
                    node_config: optionalValue,
                    filemask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

            if (trimmedComponent.startsWith('<SHIPMENTID>')) {
                const optionalValue = trimmedComponent.substring(12); // Remove '<SHIPMENTID>'
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.SHIPMENT_NODE,
                    node_config: optionalValue,
                    filemask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

            if (trimmedComponent.startsWith('<INVOICEID>')) {
                const optionalValue = trimmedComponent.substring(11); // Remove '<INVOICEID>'
                const rowData = new RowCustomFilename({
                    sequence: sequence++,
                    node_name: EdiCustomFilename.INVOICE_NODE,
                    node_config: optionalValue,
                    filemask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });                continue;
            }
        }

        // Update delimiter tooltip after parsing is complete
        this.setNodeDelimiterTooltip(this.textboxNodeDelimiter.selectedItem?.value || this.textboxNodeDelimiter.text);
    }


    override onLoad() {
        this.setupUnboundTableMethods();

        // Add selection listener to handle row selection/deselection events
        this.tableFilename.addSelectionListener((event: TableSelectionEvent) => {
            this.handleRowSelectionChange(event);
        });
        // Set initial tooltip for node delimiter based on current value after setup
        setTimeout(() => {
            this.setNodeDelimiterTooltip(this.textboxNodeDelimiter.selectedItem?.value || this.textboxNodeDelimiter.text);
        }, 0);
    }

    /**
     * Set up the necessary method overrides for the unbound table to support quick add functionality
     */
    private setupUnboundTableMethods() {
        // Store original methods
        const originalAddRow = this.tableFilename.addRow.bind(this.tableFilename);

        // Override _createNewRowData to provide proper data structure for unbound tables
        this.tableFilename._createNewRowData = async (): Promise<ModelRow | any> => {
            // For unbound tables, create a proper ModelRow instance using the RowCustomFilename class
            const newRow = new RowCustomFilename();
            return newRow;
        };

        // Override addRow to handle unbound tables by ensuring data array is initialized
        this.tableFilename.addRow = (data: any, rowProps?: Partial<TableRowProps>, options?: Partial<TableAddRowOptions>): TableAddRowResult => {
            // Initialize the data array if it doesn't exist (for unbound tables)
            if (this.tableFilename.data == null) {
                (this.tableFilename as any)._data = [];
            }

            // Call the original implementation
            return originalAddRow(data, rowProps, options);
        };

        // Override saveQuickAddData to handle the quick add functionality for unbound tables
        this.tableFilename.saveQuickAddData = () => {
            if (this.tableFilename.quickAddRow.validateSimple() !== true)
                return;

            const quickAddData = this.tableFilename.quickAddData;
            const quickAddRow = this.tableFilename.quickAddRow;

            // Update the data from the quick add row components
            quickAddRow.updateDataFromComponents();

            // Set sequence based on current table size + 1
            const currentRowCount = this.tableFilename.data ? this.tableFilename.data.length : 0;
            quickAddData.set("sequence", currentRowCount + 1);

            // Remove the current quick add row
            this.tableFilename.removeQuickAddRow();

            // Add the data as a regular row
            this.tableFilename.addRow(quickAddData, { mode: TableRowMode.NONE }, {
                display: true,
                addToData: true,
                save: false
            });

            // Create a new quick add row asynchronously
            this.tableFilename.resetQuickAddRow();
        };
    }

    /**
     * Handles row selection changes to ensure checkbox printable property stays false
     */
    private handleRowSelectionChange(event: TableSelectionEvent): void {
        // Use setTimeout to ensure this runs after any other selection handling code
        setTimeout(() => {
            this.ensureCheckboxesNonPrintable();
        }, 0);
    }

    /**
     * Ensures all exclude delimiter checkboxes are non-printable
     */
    private ensureCheckboxesNonPrintable(): void {
        const allRows = this.tableFilename.allRows;
        for (const row of allRows) {
            const checkboxExcludeDelimiter = row.findComponentById("checkboxDelimitTime") as Textbox;
            if (checkboxExcludeDelimiter) {
                // Force the printable property to false
                checkboxExcludeDelimiter.printable = false;

                // Override the printable setter to always return false
                const originalPrintableDescriptor = Object.getOwnPropertyDescriptor(checkboxExcludeDelimiter, 'printable') ||
                    Object.getOwnPropertyDescriptor(Object.getPrototypeOf(checkboxExcludeDelimiter), 'printable');

                if (originalPrintableDescriptor && !checkboxExcludeDelimiter.hasOwnProperty('_printableOverridden')) {
                    Object.defineProperty(checkboxExcludeDelimiter, 'printable', {
                        get: () => false,
                        set: (value: boolean) => {
                            // Ignore any attempts to set printable to true
                        },
                        enumerable: true,
                        configurable: true
                    });
                    (checkboxExcludeDelimiter as any)._printableOverridden = true;
                }
            }
        }
    }


    /** This is an event handler for the onRowDisplay event of tableFilename. */
    tableFilenameOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.getTableRow() as TableRow;
        const data = row.data;
        const textboxNodeName = row.findComponentById("textboxNodeName") as Textbox;
        const checkboxExcludeDelimiter = row.findComponentById("checkboxDelimitTime") as Textbox;
        const items: DropdownItem[] = [
            new DropdownItem(EdiCustomFilename.BATCH_NODE, "Batch" ),
            new DropdownItem(EdiCustomFilename.DATE_NODE, "Date"),
            new DropdownItem(EdiCustomFilename.TIME_NODE, "Time"),
            new DropdownItem(EdiCustomFilename.OPTIONAL_NODE, "Text")
        ];
        if (this.isImageType) {
            items.push(new DropdownItem(EdiCustomFilename.BOL_NODE, "BOL"));
            items.push(new DropdownItem(EdiCustomFilename.SHIPMENT_NODE, "Shipment"));
            items.push(new DropdownItem(EdiCustomFilename.ORDER_NODE, "Order"));
            items.push(new DropdownItem(EdiCustomFilename.INVOICE_NODE, "Invoice"));
        }
        if (textboxNodeName) {
            textboxNodeName.items = items;
            if (!StringUtil.isEmptyString(data.get("node_name"))) {
                textboxNodeName.setSelectedItemFromValue(data.get("node_name"));
            }
        }
        // Set the sequence textbox to the row index (1-based)
        const sequenceTextbox = row.findComponentById("textboxSequence") as Textbox;
        if (sequenceTextbox) {
            // Get the row index from the table
            const allRows = this.tableFilename.allRows;
            const rowIndex = allRows.indexOf(row);
            if (rowIndex >= 0) {
                sequenceTextbox.text = rowIndex + 1 + "";
                row.data.set("sequence", rowIndex + 1);
            }
        }

        // Get the node type from data or from the dropdown component
        let nodeType = row.data.get("node_name");

        // If no value in data, try to get it from the dropdown component
        if (!nodeType) {
            if (textboxNodeName && textboxNodeName.selectedItem) {
                nodeType = textboxNodeName.selectedItem.value;
            }
        }

        // Initialize checkbox printable state - always false (non-interactive)
        if (checkboxExcludeDelimiter) {
            checkboxExcludeDelimiter.printable = false;
        }

        // Only call the visibility update if we have a valid nodeType
        if (nodeType) {
            this.updateComponentVisibilityBasedOnNodeType(row, nodeType);
        }

        // Ensure checkbox stays non-printable after any other processing
        setTimeout(() => {
            this.ensureCheckboxesNonPrintable();
        }, 0);
    }

    /** This is an event handler for the afterDropdownSelection event of textboxNodeName. */
    textboxNodeNameAfterDropdownSelection(event: DropdownSelectionEvent) {
        const textboxNodeName = event.target as Textbox;
        const value = textboxNodeName.selectedItem.value;
        // Find the textboxNumOfDays component in the current row
        const row = TableRow.getContainingTableRow(textboxNodeName) as TableRow;

        this.updateComponentVisibilityBasedOnNodeType(row, value);
    }

    /**
     * Updates the visibility of date/time configuration components based on the selected node type
     * @param row The table row containing the components
     * @param nodeType The selected node type value ("D" for date, "T" for time, etc.)
     */
    private updateComponentVisibilityBasedOnNodeType(row: TableRow, nodeType: string): void {
        const textboxDateTime = row.findComponentById("textboxDateTimeConfig") as Textbox;
        const textboxTimeConfig = row.findComponentById("textboxTimeConfig") as Textbox;
        const checkDelimiter = row.findComponentById("checkboxDelimitTime") as Textbox;
        const textboxOptionalValue = row.findComponentById("textboxOptionalValue") as Textbox;

        // Get the stored configuration value

        const nodeConfig = row.data.get("node_config") || "";

        if (textboxDateTime) {
            let dateConfig = row.data.get("date_config") || "";
            if (StringUtil.isEmptyString(dateConfig)) {
                dateConfig = nodeConfig;
            }
            textboxDateTime.visible = nodeType === EdiCustomFilename.DATE_NODE;
            if (nodeType === EdiCustomFilename.DATE_NODE && dateConfig) {
                textboxDateTime.setSelectedItemFromValue(dateConfig);
            } else if (nodeType === EdiCustomFilename.DATE_NODE) {
                textboxDateTime.setSelectedItemFromValue("yyyyMMdd");
            }
        }

        if (textboxTimeConfig) {
            let timeConfig = row.data.get("time_config") || "";
            if (StringUtil.isEmptyString(timeConfig)) {
                timeConfig = nodeConfig;
            }
            textboxTimeConfig.visible = nodeType === EdiCustomFilename.TIME_NODE;
            if (nodeType === EdiCustomFilename.TIME_NODE && timeConfig) {
                textboxTimeConfig.setSelectedItemFromValue(timeConfig);
            } else if (nodeType === EdiCustomFilename.TIME_NODE) {
                textboxTimeConfig.setSelectedItemFromValue("HHMMSS");
            }
        }

        if (checkDelimiter) {
            checkDelimiter.visible = nodeType === EdiCustomFilename.DATE_NODE;
            // Always set printable to false (non-interactive)
            checkDelimiter.printable = false;
        }

        // Ensure all checkboxes stay non-printable
        setTimeout(() => {
            this.ensureCheckboxesNonPrintable();
        }, 0);

        if (textboxOptionalValue) {
            textboxOptionalValue.required = nodeType === EdiCustomFilename.OPTIONAL_NODE;
            textboxOptionalValue.requiredDuringAdd = nodeType === EdiCustomFilename.OPTIONAL_NODE;
            textboxOptionalValue.requiredDuringUpdate = nodeType === EdiCustomFilename.OPTIONAL_NODE;
            textboxOptionalValue.visible = nodeType === EdiCustomFilename.OPTIONAL_NODE;
            if (nodeType === EdiCustomFilename.OPTIONAL_NODE && nodeConfig) {
                textboxOptionalValue.text = nodeConfig;
            }
        }
    }

    generateFileMask(): string {
        let fileMask = "";
        const maskComponents: string[] = [];
        let excludeDelimiter = false;

        // Add prefix if populated
        const prefixValue = this.textboxPrefix.text;
        if (prefixValue && prefixValue.trim()) {
            maskComponents.push(`<PRE>${prefixValue}`);
        }

        // Loop through table rows to build mask components
        const tableRows = this.tableFilename.allRows;
        for (const row of tableRows) {
            const nodeType = row.data.get("node_name");
            if (!nodeType) continue;

            switch (nodeType) {
                case EdiCustomFilename.SHIPMENT_NODE:
                    maskComponents.push("<SHIPMENTID>ShipmentID");
                    break;

                case EdiCustomFilename.BOL_NODE:
                    maskComponents.push("<BOL>BOL");
                    break;

                case EdiCustomFilename.ORDER_NODE:
                    maskComponents.push("<ORDERID>OrderID");
                    break;

                case EdiCustomFilename.INVOICE_NODE:
                    maskComponents.push("<INVOICEID>InvoiceID");
                    break;

                case EdiCustomFilename.BATCH_NODE: // Batch
                    maskComponents.push("<BATCHNO>");
                    break;

                case EdiCustomFilename.DATE_NODE: // Date/Time
                    // Get date config from node_config or component
                    let dateConfig = row.data.get("node_config") || row.data.get("date_config") || "";
                    const textboxDateTime = row.findComponentById("textboxDateTimeConfig") as Textbox;
                    if (textboxDateTime && textboxDateTime.selectedItem) {
                        dateConfig = textboxDateTime.selectedItem.value;
                    }

                    let dateTag = "<DATE>";
                    if (dateConfig && dateConfig.trim()) {
                        dateTag = `<DATE>${dateConfig}`;
                    }

                    // Check for exclude delimiter setting
                    const exd = row.data.get("exclude_delimiter");
                    if (exd === "Y") {
                        excludeDelimiter = true;
                    }

                    maskComponents.push(dateTag);
                    break;

                case EdiCustomFilename.TIME_NODE: // Time
                    // Get time config from node_config or component
                    let timeConfig = row.data.get("node_config") || row.data.get("time_config") || "";
                    const textboxTimeConfig = row.findComponentById("textboxTimeConfig") as Textbox;
                    if (textboxTimeConfig && textboxTimeConfig.selectedItem) {
                        timeConfig = textboxTimeConfig.selectedItem.value;
                    }

                    if (timeConfig && timeConfig.trim()) {
                        maskComponents.push(`<TIME>${timeConfig}`);
                    } else {
                        maskComponents.push("<TIME>");
                    }
                    break;

                case EdiCustomFilename.OPTIONAL_NODE: // Optional Node
                    // Get optional value from node_config or component
                    let optionalValue = row.data.get("node_config") || row.data.get("optional_value") || "";
                    const textboxOptionalValue = row.findComponentById("textboxOptionalValue") as Textbox;
                    if (textboxOptionalValue) {
                        optionalValue = textboxOptionalValue.text;
                    }

                    if (optionalValue && optionalValue.trim()) {
                        maskComponents.push(`<OPT>${optionalValue}`);
                    } else {
                        maskComponents.push("<OPT>");
                    }
                    break;
            }
        }

        // Add extension if populated
        const extensionValue = this.textboxExtension.text;
        if (extensionValue && extensionValue.trim()) {
            maskComponents.push(`<SUF>${extensionValue}`);
        }

        // Always add delimiter tag (whether it has a value or not)
        // Use selectedItem.value if available, otherwise fall back to text
        const delimiterValue = this.textboxNodeDelimiter.selectedItem?.value || this.textboxNodeDelimiter.text;
        if (delimiterValue && delimiterValue.trim() && maskComponents.length > 0) {
            maskComponents.push(`<DEL>${delimiterValue}`);
        } else {
            if(maskComponents.length > 0){
                maskComponents.push("<DEL>");
            }
        }

        // Add EXCLDATEDEL tag if exclude date delimiter is set
        if (excludeDelimiter) {
            maskComponents.push("<EXCLDATEDEL>");
        }

        // Join components with ":"
        fileMask = maskComponents.join(":");

        return fileMask;
    }

    /** This is an event handler for the onClick event of buttonTestFilename. */
    buttonTestFilenameOnClick(event: ClickEvent) {
        const fileMask = this.generateFileMask();
        // this.textboxSampleFilename.text = fileMask;//Temporary -- remove after call to API done

        // Use Api.search directly to avoid affecting the table's data source
        // Use the same endpoint path as the data source but make an independent API call
        if (this.validateForbiddenCharacters()) {
            Api.search("lme/datafusion/custom-filename", {
                file_mask: fileMask,
                batchno: this.batchno
            }).then((result) => {
                if (result && result.data && result.data.length > 0) {
                    this.textboxSampleFilename.text = result.data[0]["filename"];
                }
                // Handle successful search results if needed
            }).catch((error) => {
                // Handle errors if needed
                console.error("Error searching custom filename:", error);
            });
        }

    }


    /**
     * Validates that none of the components contain forbidden characters
     * @returns true if validation passes, false if any forbidden characters are found
     */
    validateForbiddenCharacters(): boolean {
        // Check textboxPrefix
        const prefixText = this.textboxPrefix.text || "";
        for (const char of prefixText) {
            if (this._forbidden.has(char)) {
                CommonDialogs.showMessage(
                    `The Prefix field contains the forbidden character: '${char}'`,
                    "Validation Error"
                );
                return false;
            }
        }

        // Check textboxExtension
        const extensionText = this.textboxExtension.text || "";
        for (const char of extensionText) {
            if (this._forbidden.has(char)) {
                CommonDialogs.showMessage(
                    `The Extension field contains the forbidden character: '${char}'`,
                    "Validation Error"
                );
                return false;
            }
        }

        // Check textboxOptionalValue in each table row
        const tableRows = this.tableFilename.allRows;
        for (let i = 0; i < tableRows.length; i++) {
            const row = tableRows[i];
            const textboxOptionalValue = row.findComponentById("textboxOptionalValue") as Textbox;

            if (textboxOptionalValue) {
                const optionalText = textboxOptionalValue.text || "";
                for (const char of optionalText) {
                    if (this._forbidden.has(char)) {
                        CommonDialogs.showMessage(
                            `Row ${i + 1} Optional Value field contains the forbidden character: '${char}'`,
                            "Validation Error"
                        );
                        return false;
                    }
                }
            }
        }

        return true;
    }

    /**
     * Override the base validate method to include forbidden character validation
     */
    validate(checkRequired?: boolean, showErrors?: boolean) {
        // First run the standard validation from the parent class
        const validationResults = super.validate(checkRequired, showErrors);

        // Then run our custom forbidden character validation
        // If forbidden characters are found, the method will display a dialog and return false
        // We don't need to add to validationResults since we're showing our own dialog
        if (!this.validateForbiddenCharacters()) {
            // Return the existing results plus a custom validation failure
            // This ensures the form doesn't submit when forbidden characters are found
            validationResults.push({
                isValid: false,
                validationMessage: "Forbidden characters detected",
                caption: "Validation Error",
                component: null
            });
        }

        return validationResults;
    }


/** This is an event handler for the afterDropdownSelection event of textboxNodeDelimiter. */
  textboxNodeDelimiterAfterDropdownSelection(event: DropdownSelectionEvent) {
    const textboxNodeDelimiter = event.target as Textbox;
    const selectedValue = textboxNodeDelimiter.selectedItem?.value;

    this.setNodeDelimiterTooltip(selectedValue);
  }

    /**
     * Sets the tooltip for the textboxNodeDelimiter based on the selected value
     * @param selectedValue The selected delimiter value
     */
    private setNodeDelimiterTooltip(selectedValue: string | undefined): void {
        // Set tooltip based on selected value
        if (selectedValue === "-") {
            // Hyphen character
            this.textboxNodeDelimiter.tooltip = "hyphen";
        } else if (selectedValue === ".") {
            // Period character
            this.textboxNodeDelimiter.tooltip = "period";
        } else if (selectedValue === "_") {
            // Underscore character
            this.textboxNodeDelimiter.tooltip = "underscore";
        } else {
            // For any other values (including empty string), set to blank
            this.textboxNodeDelimiter.tooltip = "blank";
        }
    }

    /** This is an event handler for the onClick event of buttonClear. */
    buttonClearOnClick(event: ClickEvent) {
        // Clear the prefix
        this.textboxPrefix.text = "";

        // Clear the delimiter selected value (select blank)
        this.textboxNodeDelimiter.selectedItem = null;
        this.textboxNodeDelimiter.text = "";
        this.setNodeDelimiterTooltip("");

        // Delete all table rows in tableFilename
        this.tableFilename.clearRows();

        // Delete the extension
        this.textboxExtension.text = "";
    }
}
